'use client'
import { createClient } from '@/lib/supabase/client'

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  CreditCard,
  QrCode,
  Barcode,
  Check,
  Loader2,
  Copy,
  Clock,
  AlertCircle,
  Lock,
  Download,
  ChevronDown,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TransparentCheckoutProps {
  // Modo 1: canal de venda dinamico (novo, preferido)
  // Se informado, o componente busca config em /api/checkout/config?slug=X
  // e sobrescreve productType, productId, amount, allowBoleto, maxInstallments,
  // requireAddress automaticamente.
  channelSlug?: string
  // Modo 2: hardcoded (legado, sera removido quando todas as landings migrarem)
  productType?: 'plan' | 'book' | 'credit_pack' | 'course' | 'event' | 'mentoria'
  productId?: string
  amount?: number // centavos (preço do produto, SEM frete)
  /**
   * Valor do frete em centavos. Quando > 0, é somado ao amount no total
   * cobrado e enviado separado pro backend (que aplica split 100% pra Paty).
   * Default 0 (sem frete).
   */
  shippingCents?: number
  /** Nome do serviço de envio selecionado (ex: 'PAC', 'SEDEX'). Apenas display. */
  shippingService?: string
  title?: string
  description?: string
  buyerName?: string
  buyerEmail?: string
  buyerCpf?: string
  buyerPhone?: string
  onSuccess: (paymentId: string) => void
  onError: (error: string) => void
  allowBoleto?: boolean
  maxInstallments?: number
  requireAddress?: boolean
  hideCustomerForm?: boolean
  prefilledAddress?: {
    street: string
    number: string
    complement?: string
    neighborhood: string
    city: string
    state: string
    zipCode: string
    recipientName?: string
  }
  // Modo de cobranca:
  // - 'payment' (default): pagamento unico (cobranca avulsa no Asaas)
  // - 'subscription': assinatura recorrente mensal (so aceita CREDIT_CARD)
  mode?: 'payment' | 'subscription'
}

type BillingType = 'CREDIT_CARD' | 'PIX' | 'BOLETO'

type CheckoutState =
  | 'idle'
  | 'filling_info'
  | 'selecting_method'
  | 'processing'
  | 'success'
  | 'error'

interface CustomerInfo {
  name: string
  email: string
  cpfCnpj: string
  phone: string
}

interface AddressInfo {
  cep: string
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  recipient: string
}

interface CardInfo {
  holderName: string
  number: string
  expiry: string
  cvv: string
  cpfCnpj: string
}

interface PixData {
  paymentId: string
  qrCodeImage: string
  qrCodeText: string
  expiresAt: string
}

interface BoletoData {
  paymentId: string
  bankSlipUrl: string
  identificationField: string
  dueDate: string
}

// ---------------------------------------------------------------------------
// Masks
// ---------------------------------------------------------------------------

function maskCpf(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`
  if (digits.length <= 9)
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`
}

function maskCnpj(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14)
  if (digits.length <= 2) return digits
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`
  if (digits.length <= 8)
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`
  if (digits.length <= 12)
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`
}

function maskCpfCnpj(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.length <= 11) return maskCpf(value)
  return maskCnpj(value)
}

function normalizePhone(value: string): string {
  const d = value.replace(/\D/g, '')
  if (d.length === 11) return '55' + d
  if (d.length >= 12 && d.startsWith('55')) return d.slice(0, 13)
  return '55' + d
}

function maskPhone(value: string): string {
  let digits = value.replace(/\D/g, '')
  if (digits.length >= 12 && digits.startsWith('55')) digits = digits.slice(2)
  digits = digits.slice(0, 11)
  if (digits.length <= 2) return digits.length ? `(${digits}` : ''
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
}

function maskCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 16)
  return digits.replace(/(.{4})/g, '$1 ').trim()
}

function maskExpiry(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 4)
  if (digits.length <= 2) return digits
  return `${digits.slice(0, 2)}/${digits.slice(2)}`
}

function maskCep(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 5) return digits
  return `${digits.slice(0, 5)}-${digits.slice(5)}`
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(cents / 100)
}

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '')
}

function detectCardBrand(number: string): string | null {
  const digits = onlyDigits(number)
  if (!digits) return null
  if (/^4/.test(digits)) return 'visa'
  if (/^5[1-5]/.test(digits) || /^2[2-7]/.test(digits)) return 'mastercard'
  if (/^3[47]/.test(digits)) return 'amex'
  if (/^(636368|438935|504175|451416|636297|5067|4576|4011|506699)/.test(digits))
    return 'elo'
  return null
}

function validateCpf(cpf: string): boolean {
  const digits = onlyDigits(cpf)
  if (digits.length !== 11) return false
  if (/^(\d)\1+$/.test(digits)) return false
  let sum = 0
  for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i)
  let rest = (sum * 10) % 11
  if (rest === 10) rest = 0
  if (rest !== parseInt(digits[9])) return false
  sum = 0
  for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i)
  rest = (sum * 10) % 11
  if (rest === 10) rest = 0
  return rest === parseInt(digits[10])
}

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function validateExpiry(expiry: string): boolean {
  const parts = expiry.split('/')
  if (parts.length !== 2) return false
  const month = parseInt(parts[0])
  const year = parseInt(parts[1])
  if (month < 1 || month > 12) return false
  const now = new Date()
  const currentYear = now.getFullYear() % 100
  const currentMonth = now.getMonth() + 1
  if (year < currentYear) return false
  if (year === currentYear && month < currentMonth) return false
  return true
}

const BRAZILIAN_STATES = [
  'AC','AL','AM','AP','BA','CE','DF','ES','GO','MA',
  'MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN',
  'RO','RR','RS','SC','SE','SP','TO',
]

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CardBrandIcon({ brand }: { brand: string | null }) {
  if (!brand) return null
  const labels: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Master',
    amex: 'Amex',
    elo: 'Elo',
  }
  return (
    <span className="text-xs font-semibold text-brand-600 bg-brand-50 px-2 py-0.5 rounded">
      {labels[brand] ?? brand}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function TransparentCheckout({
  channelSlug,
  productType: propProductType,
  productId: propProductId,
  amount: propAmount,
  shippingCents = 0,
  shippingService,
  title: propTitle,
  description: propDescription,
  buyerName = '',
  buyerEmail = '',
  buyerCpf = '',
  buyerPhone = '',
  onSuccess,
  onError,
  allowBoleto: propAllowBoleto = true,
  maxInstallments: propMaxInstallments = 1,
  requireAddress: propRequireAddress = false,
  hideCustomerForm = false,
  prefilledAddress,
  mode = 'payment',
}: TransparentCheckoutProps) {
  const isSubscription = mode === 'subscription'
  // ---- Canal dinamico ----
  // Se channelSlug foi passado, busca a config do canal e sobrescreve
  // os props hardcoded. Enquanto carrega, o form fica escondido.
  const [channelConfig, setChannelConfig] = useState<{
    channel: {
      slug: string
      name: string
      require_address: boolean
      max_installments: number
      allow_boleto: boolean
      allow_pix: boolean
      allow_card: boolean
    }
    product: {
      slug: string
      name: string
      type: 'plan' | 'book' | 'credit_pack' | 'course' | 'event' | 'mentoria'
      price_cents: number
      credits_granted: number
      cta_url: string | null
      description: string | null
    }
  } | null>(null)
  const [channelLoading, setChannelLoading] = useState<boolean>(!!channelSlug)
  const [channelError, setChannelError] = useState<string | null>(null)

  useEffect(() => {
    if (!channelSlug) return
    let cancelled = false
    setChannelLoading(true)
    setChannelError(null)
    fetch(`/api/checkout/config?slug=${encodeURIComponent(channelSlug)}`)
      .then(async r => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}))
          throw new Error(data?.error || 'Canal indisponivel')
        }
        return r.json()
      })
      .then(data => {
        if (!cancelled) {
          setChannelConfig(data)
          setChannelLoading(false)
        }
      })
      .catch(err => {
        if (!cancelled) {
          setChannelError(err.message || 'Erro ao carregar canal')
          setChannelLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [channelSlug])

  // Valores efetivos (canal > props)
  const productType = channelConfig?.product.type ?? propProductType
  const productId = channelConfig?.product.slug ?? propProductId
  const amount = channelConfig?.product.price_cents ?? propAmount
  const title = channelConfig?.product.name ?? propTitle
  const description = channelConfig?.product.description ?? propDescription
  const allowBoleto = channelConfig?.channel.allow_boleto ?? propAllowBoleto
  const maxInstallments = channelConfig?.channel.max_installments ?? propMaxInstallments
  const requireAddress = channelConfig?.channel.require_address ?? propRequireAddress

  // ---- State ----
  const [state, setState] = useState<CheckoutState>('idle')
  const [activeTab, setActiveTab] = useState<BillingType>('CREDIT_CARD')
  const [errorMessage, setErrorMessage] = useState('')
  const [successPaymentId, setSuccessPaymentId] = useState('')

  // Customer
  const [customer, setCustomer] = useState<CustomerInfo>({
    name: buyerName,
    email: buyerEmail,
    cpfCnpj: buyerCpf ? maskCpfCnpj(buyerCpf) : '',
    phone: buyerPhone ? maskPhone(buyerPhone) : '',
  })
  const [customerErrors, setCustomerErrors] = useState<Partial<CustomerInfo>>({})

  // Address
  const [address, setAddress] = useState<AddressInfo>({
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    recipient: '',
  })
  const [addressErrors, setAddressErrors] = useState<Partial<AddressInfo>>({})
  const [loadingCep, setLoadingCep] = useState(false)

  // Card
  const [card, setCard] = useState<CardInfo>({
    holderName: '',
    number: '',
    expiry: '',
    cvv: '',
    cpfCnpj: '',
  })
  const [cardErrors, setCardErrors] = useState<Partial<CardInfo>>({})
  const [installments, setInstallments] = useState(1)

  // PIX
  const [pixData, setPixData] = useState<PixData | null>(null)
  const [pixExpired, setPixExpired] = useState(false)
  const [pixTimeLeft, setPixTimeLeft] = useState(0)
  const [loadingPix, setLoadingPix] = useState(false)

  // Boleto
  const [boletoData, setBoletoData] = useState<BoletoData | null>(null)
  const [loadingBoleto, setLoadingBoleto] = useState(false)

  // Card billing address (CEP + número — mínimo exigido pelo Asaas pra cartão)
  // Usado quando requireAddress=false (produtos digitais) e método=CARTÃO.
  const [cardAddress, setCardAddress] = useState({ cep: '', number: '' })
  const [cardAddressErrors, setCardAddressErrors] = useState<Partial<{ cep: string; number: string }>>({})

  // Clipboard
  const [copiedField, setCopiedField] = useState<string | null>(null)

  // Polling ref
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // ---- Helpers ----

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const supabase = createClient()
      const { data: { session } } = await supabase.auth.getSession()
      return session?.access_token ?? null
    } catch {
      return null
    }
  }, [])

  const copyToClipboard = useCallback(async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch {
      const el = document.createElement('textarea')
      el.value = text
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    }
  }, [])

  // ---- Validation ----

  function validateCustomer(): boolean {
    const errors: Partial<CustomerInfo> = {}
    if (!customer.name.trim() || customer.name.trim().length < 3)
      errors.name = 'Informe seu nome completo'
    if (!validateEmail(customer.email)) errors.email = 'E-mail inválido'
    const cpfDigits = onlyDigits(customer.cpfCnpj)
    if (cpfDigits.length !== 11 && cpfDigits.length !== 14)
      errors.cpfCnpj = 'CPF ou CNPJ inválido'
    else if (cpfDigits.length === 11 && !validateCpf(customer.cpfCnpj))
      errors.cpfCnpj = 'CPF inválido'
    const phoneDigits = onlyDigits(customer.phone)
    if (phoneDigits.length < 10 || phoneDigits.length > 11)
      errors.phone = 'Telefone inválido'
    setCustomerErrors(errors)
    return Object.keys(errors).length === 0
  }

  function validateAddress(): boolean {
    if (!requireAddress && !prefilledAddress) return true
    if (prefilledAddress) return true
    const errors: Partial<AddressInfo> = {}
    if (onlyDigits(address.cep).length !== 8) errors.cep = 'CEP inválido'
    if (!address.street.trim()) errors.street = 'Informe a rua'
    if (!address.number.trim()) errors.number = 'Informe o número'
    if (!address.neighborhood.trim()) errors.neighborhood = 'Informe o bairro'
    if (!address.city.trim()) errors.city = 'Informe a cidade'
    if (!address.state) errors.state = 'Selecione o estado'
    setAddressErrors(errors)
    return Object.keys(errors).length === 0
  }

  function validateCardAddress(): boolean {
    // Se já tem endereço completo (livro/físico), não precisa destes campos extras
    if (requireAddress || prefilledAddress) return true
    const errors: Partial<{ cep: string; number: string }> = {}
    if (onlyDigits(cardAddress.cep).length !== 8) errors.cep = 'CEP obrigatório para cartão'
    if (!cardAddress.number.trim()) errors.number = 'Número obrigatório para cartão'
    setCardAddressErrors(errors)
    return Object.keys(errors).length === 0
  }

  function validateCard(): boolean {
    const errors: Partial<CardInfo> = {}
    if (!card.holderName.trim() || card.holderName.trim().length < 3)
      errors.holderName = 'Informe o nome impresso no cartão'
    if (onlyDigits(card.number).length < 13)
      errors.number = 'Número do cartão inválido'
    if (!validateExpiry(card.expiry)) errors.expiry = 'Validade inválida'
    const cvvLen = onlyDigits(card.cvv).length
    if (cvvLen < 3 || cvvLen > 4) errors.cvv = 'CVV inválido'
    const cpfDigits = onlyDigits(card.cpfCnpj)
    if (cpfDigits.length !== 11 && cpfDigits.length !== 14)
      errors.cpfCnpj = 'CPF/CNPJ do titular inválido'
    setCardErrors(errors)
    return Object.keys(errors).length === 0
  }

  // ---- CEP auto-fill ----

  async function fetchCep(cep: string) {
    const digits = onlyDigits(cep)
    if (digits.length !== 8) return
    setLoadingCep(true)
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`)
      const data = await res.json()
      if (!data.erro) {
        setAddress((prev) => ({
          ...prev,
          street: data.logradouro || prev.street,
          neighborhood: data.bairro || prev.neighborhood,
          city: data.localidade || prev.city,
          state: data.uf || prev.state,
        }))
      }
    } catch {
      // silently ignore CEP fetch errors
    } finally {
      setLoadingCep(false)
    }
  }

  // ---- PIX countdown ----

  useEffect(() => {
    if (!pixData || pixExpired) return
    const rawTarget = new Date(pixData.expiresAt).getTime()
    // Limitar a 30 minutos (Asaas pode retornar datas muito distantes)
    const maxExpiry = Date.now() + 30 * 60 * 1000
    const target = Math.min(rawTarget, maxExpiry)
    const interval = setInterval(() => {
      const diff = Math.max(0, Math.floor((target - Date.now()) / 1000))
      setPixTimeLeft(diff)
      if (diff <= 0) {
        setPixExpired(true)
        clearInterval(interval)
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [pixData, pixExpired])

  // ---- PIX polling ----

  useEffect(() => {
    if (!pixData || pixExpired || state === 'success') return

    pollingRef.current = setInterval(async () => {
      try {
        // guest checkout (comprador novo, sem login) tambem precisa confirmar
        // o PIX: envia Authorization so quando existir token
        const token = await getAccessToken()
        const res = await fetch(
          `/api/checkout/status?paymentId=${pixData.paymentId}`,
          { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
        )
        const data = await res.json()
        if (data.status === 'CONFIRMED' || data.status === 'RECEIVED') {
          setState('success')
          setSuccessPaymentId(pixData.paymentId)
          onSuccess(pixData.paymentId)
          if (pollingRef.current) clearInterval(pollingRef.current)
        }
      } catch {
        // silently ignore polling errors
      }
    }, 5000)

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [pixData, pixExpired, state, getAccessToken, onSuccess])

  // ---- Cleanup polling on unmount ----
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [])

  // ---- API calls ----

  async function createPayment(billingType: BillingType) {
    const token = await getAccessToken()
    // Token opcional - guest checkout permitido (livro, oferta, planos)

    // ============================================================
    // SUBSCRIPTION MODE: endpoint + payload separados
    // Suporta CREDIT_CARD (tokenizado inline), PIX e BOLETO (1a fatura
    // enviada por email + QR/boleto retornado pra pagamento imediato)
    // ============================================================
    if (isSubscription) {
      const subPayload: Record<string, unknown> = {
        productId: productId!,
        billingType,
        customer: {
          name: customer.name.trim(),
          email: customer.email.trim(),
          cpfCnpj: onlyDigits(customer.cpfCnpj),
          phone: normalizePhone(customer.phone),
        },
      }

      if (billingType === 'CREDIT_CARD') {
        const [expiryMonth, expiryYear] = card.expiry.split('/')
        subPayload.creditCard = {
          holderName: card.holderName.trim(),
          number: onlyDigits(card.number),
          expiryMonth,
          expiryYear,
          ccv: card.cvv,
        }
        subPayload.creditCardHolderInfo = {
          name: card.holderName.trim(),
          cpfCnpj: onlyDigits(card.cpfCnpj),
          email: customer.email.trim(),
          phone: normalizePhone(customer.phone),
          postalCode: prefilledAddress
            ? onlyDigits(prefilledAddress.zipCode)
            : requireAddress
              ? onlyDigits(address.cep)
              : onlyDigits(cardAddress.cep),
          addressNumber: prefilledAddress
            ? prefilledAddress.number.trim()
            : requireAddress
              ? address.number.trim()
              : cardAddress.number.trim(),
        }
      }

      try {
        const res = await fetch('/api/checkout/subscription/transparent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(subPayload),
        })
        const data = await res.json()
        if (!res.ok) {
          throw new Error(data.error || 'Erro ao criar assinatura')
        }
        return data
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erro ao criar assinatura'
        setErrorMessage(message)
        setState('error')
        onError(message)
        return null
      }
    }

    // ============================================================
    // PAYMENT MODE: fluxo original
    // ============================================================
    // Frete: somado ao value total; backend usa shippingCents separado
    // para aplicar split correto (100% do frete vai pra org parceira).
    const totalCents = (amount ?? 0) + (shippingCents || 0)
    const payload: Record<string, unknown> = {
      ...(channelSlug ? { channelSlug } : {}),
      productType,
      productId,
      billingType,
      value: totalCents / 100,
      ...(shippingCents && shippingCents > 0
        ? {
            shippingCents,
            ...(shippingService ? { shippingService } : {}),
          }
        : {}),
      customer: {
        name: customer.name.trim(),
        email: customer.email.trim(),
        cpfCnpj: onlyDigits(customer.cpfCnpj),
        phone: normalizePhone(customer.phone),
      },
    }

    if (billingType === 'CREDIT_CARD') {
      const [expiryMonth, expiryYear] = card.expiry.split('/')
      payload.creditCard = {
        holderName: card.holderName.trim(),
        number: onlyDigits(card.number),
        expiryMonth,
        expiryYear,
        ccv: card.cvv,
      }
      payload.creditCardHolderInfo = {
        name: card.holderName.trim(),
        cpfCnpj: onlyDigits(card.cpfCnpj),
        email: customer.email.trim(),
        phone: normalizePhone(customer.phone),
        // CEP + número: do endereço completo (se requireAddress) ou do cardAddress (mínimo pra cartão)
        postalCode: prefilledAddress
          ? onlyDigits(prefilledAddress.zipCode)
          : requireAddress
            ? onlyDigits(address.cep)
            : onlyDigits(cardAddress.cep),
        addressNumber: prefilledAddress
          ? prefilledAddress.number.trim()
          : requireAddress
            ? address.number.trim()
            : cardAddress.number.trim(),
      }
      if (installments > 1) {
        payload.installmentCount = installments
      }
    }

    if (prefilledAddress) {
      payload.address = {
        postalCode: onlyDigits(prefilledAddress.zipCode),
        street: prefilledAddress.street.trim(),
        number: prefilledAddress.number.trim(),
        complement: (prefilledAddress.complement || '').trim(),
        neighborhood: prefilledAddress.neighborhood.trim(),
        city: prefilledAddress.city.trim(),
        state: prefilledAddress.state,
        recipient: (prefilledAddress.recipientName || customer.name).trim(),
      }
    } else if (requireAddress) {
      payload.address = {
        postalCode: onlyDigits(address.cep),
        street: address.street.trim(),
        number: address.number.trim(),
        complement: address.complement.trim(),
        neighborhood: address.neighborhood.trim(),
        city: address.city.trim(),
        state: address.state,
        recipient: address.recipient.trim() || customer.name.trim(),
      }
    }

    try {
      const res = await fetch('/api/checkout/transparent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (!res.ok) {
        throw new Error(data.error || 'Erro ao processar pagamento')
      }
      return data
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Erro ao processar pagamento'
      setErrorMessage(message)
      setState('error')
      onError(message)
      return null
    }
  }

  // ---- Handlers ----

  async function handleCardPayment() {
    if (!validateCustomer() || !validateAddress() || !validateCardAddress() || !validateCard()) return

    setState('processing')
    setErrorMessage('')

    const data = await createPayment('CREDIT_CARD')
    if (!data) return

    if (data.status === 'CONFIRMED' || data.status === 'RECEIVED') {
      setState('success')
      setSuccessPaymentId(data.paymentId)
      onSuccess(data.paymentId)
    } else if (data.status === 'REFUSED' || data.status === 'FAILED') {
      setErrorMessage(
        data.failReason || 'Cartão recusado. Verifique os dados e tente novamente.'
      )
      setState('error')
      onError(data.failReason || 'Cartão recusado')
    } else {
      setState('success')
      setSuccessPaymentId(data.paymentId)
      onSuccess(data.paymentId)
    }
  }

  async function handlePixPayment() {
    if (!validateCustomer() || !validateAddress()) return

    setLoadingPix(true)
    setPixExpired(false)
    setPixData(null)
    setState('processing')
    setErrorMessage('')

    const data = await createPayment('PIX')
    if (!data) {
      setLoadingPix(false)
      return
    }

    const pix = data.pix || {}
    setPixData({
      paymentId: data.paymentId,
      qrCodeImage: pix.qrCodeImage || data.pixQrCodeImage || data.qrCodeImage || '',
      qrCodeText: pix.copyPaste || data.pixCopyPaste || data.qrCodeText || '',
      expiresAt:
        pix.expirationDate || data.pixExpiresAt ||
        new Date(Date.now() + 30 * 60 * 1000).toISOString(),
    })
    setPixTimeLeft(30 * 60)
    setLoadingPix(false)
    setState('selecting_method')
  }

  async function handleBoletoPayment() {
    if (!validateCustomer() || !validateAddress()) return

    setLoadingBoleto(true)
    setBoletoData(null)
    setState('processing')
    setErrorMessage('')

    const data = await createPayment('BOLETO')
    if (!data) {
      setLoadingBoleto(false)
      return
    }

    const boleto = data.boleto || {}
    setBoletoData({
      paymentId: data.paymentId,
      bankSlipUrl: boleto.url || data.bankSlipUrl || '',
      identificationField: boleto.barCode || data.identificationField || '',
      dueDate: boleto.dueDate || data.dueDate || '',
    })
    setLoadingBoleto(false)
    setState('selecting_method')
  }

  // ---- Installment options ----

  function getInstallmentOptions() {
    const options = []
    // Total para parcelar = produto + frete
    const installmentBase = (amount ?? 0) + (shippingCents || 0)
    for (let i = 1; i <= Math.max(1, maxInstallments); i++) {
      const value = installmentBase / i
      const label =
        i === 1
          ? `${formatCurrency(installmentBase)} à vista`
          : `${i}x de ${formatCurrency(Math.ceil(value))}`
      options.push({ value: i, label })
    }
    return options
  }

  // ---- Format time ----

  function formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  // ---- Format date ----

  function formatDate(dateStr: string): string {
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString('pt-BR')
    } catch {
      return dateStr
    }
  }

  // ---- Success screen ----

  if (state === 'success') {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-ink-0">
            {isSubscription ? 'Assinatura ativada!' : 'Pagamento confirmado!'}
          </h2>
          <p className="text-ink-4 text-sm">
            {isSubscription
              ? 'Em instantes voce recebera as credenciais no WhatsApp.'
              : 'Seu acesso será liberado em instantes.'}
          </p>
          {successPaymentId && (
            <p className="text-xs text-ink-5">
              ID: {successPaymentId}
            </p>
          )}
        </div>
      </div>
    )
  }

  // ---- Render ----

  // Guard: canal dinamico ainda carregando ou indisponivel
  if (channelSlug && channelLoading) {
    return (
      <div className="w-full max-w-lg mx-auto">
        <div className="bg-paper rounded-xl border border-ink-7 p-8 text-center shadow-sm">
          <Loader2 className="w-6 h-6 text-ink-5 animate-spin mx-auto mb-3" />
          <p className="text-sm text-ink-4">Carregando checkout...</p>
        </div>
      </div>
    )
  }
  if (channelSlug && channelError) {
    return (
      <div className="w-full max-w-lg mx-auto">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
          <p className="text-sm font-medium text-amber-900">Checkout temporariamente indisponível</p>
          <p className="text-xs text-amber-700 mt-1">Tente novamente em alguns instantes ou entre em contato com o suporte.</p>
        </div>
      </div>
    )
  }
  // Sanity: sem canal e sem productType/amount, nao tem como renderizar
  if (!productType || !productId || !amount || !title) {
    return (
      <div className="w-full max-w-lg mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-6 h-6 text-red-600 mx-auto mb-2" />
          <p className="text-sm font-medium text-red-900">Configuração de checkout ausente</p>
          <p className="text-xs text-red-700 mt-1">Canal de venda não configurado.</p>
        </div>
      </div>
    )
  }

  // Total cobrado = produto + frete (se houver). Asaas recebe esse valor.
  const productAmount = amount ?? 0
  const shipAmount = shippingCents || 0
  const totalAmount = productAmount + shipAmount

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Header */}
      <div className="bg-paper rounded-xl border border-ink-7 p-5 mb-4 shadow-sm text-center">
        <h1 className="text-xl font-bold text-ink-0">{title}</h1>
        {description && (
          <p className="text-sm text-ink-4 mt-1">{description}</p>
        )}
        {/* Resumo Subtotal + Frete (só quando há frete > 0) */}
        {shipAmount > 0 && (
          <div className="mt-3 text-left max-w-xs mx-auto space-y-1">
            <div className="flex justify-between text-sm text-ink-4">
              <span>Subtotal</span>
              <span>{formatCurrency(productAmount)}</span>
            </div>
            <div className="flex justify-between text-sm text-ink-4">
              <span>Frete{shippingService ? ` (${shippingService})` : ''}</span>
              <span>{formatCurrency(shipAmount)}</span>
            </div>
            <div className="flex justify-between text-base font-semibold text-ink-0 pt-1 border-t border-ink-7">
              <span>Total</span>
              <span>{formatCurrency(totalAmount)}</span>
            </div>
          </div>
        )}
        <div className="mt-3">
          <span className="text-2xl font-bold text-ink-0">
            {formatCurrency(totalAmount)}
            {isSubscription && <span className="text-sm text-ink-4 font-normal">/mês</span>}
          </span>
          {isSubscription ? (
            <span className="block text-sm text-ink-4 mt-0.5">
              Cobrança mensal recorrente. Cancele quando quiser.
            </span>
          ) : (
            maxInstallments > 1 && (
              <span className="block text-sm text-ink-4 mt-0.5">
                ou {maxInstallments}x de{' '}
                {formatCurrency(Math.ceil(totalAmount / maxInstallments))}
              </span>
            )
          )}
        </div>
      </div>

      {/* Customer info */}
      {!hideCustomerForm && (
      <div className="bg-paper rounded-xl border border-ink-7 p-5 mb-4 shadow-sm">
        <h3 className="text-sm font-semibold text-ink-2 mb-4">
          Seus dados
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-ink-3 mb-1">
              Nome completo
            </label>
            <input
              type="text"
              value={customer.name}
              onChange={(e) =>
                setCustomer((p) => ({ ...p, name: e.target.value }))
              }
              placeholder="Maria da Silva"
              className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                customerErrors.name
                  ? 'border-red-400 bg-red-50'
                  : 'border-ink-6'
              }`}
            />
            {customerErrors.name && (
              <p className="text-xs text-red-500 mt-1">{customerErrors.name}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-3 mb-1">
              E-mail
            </label>
            <input
              type="email"
              value={customer.email}
              onChange={(e) =>
                setCustomer((p) => ({ ...p, email: e.target.value }))
              }
              placeholder="maria@exemplo.com"
              className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                customerErrors.email
                  ? 'border-red-400 bg-red-50'
                  : 'border-ink-6'
              }`}
            />
            {customerErrors.email && (
              <p className="text-xs text-red-500 mt-1">
                {customerErrors.email}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-3 mb-1">
                CPF/CNPJ
              </label>
              <input
                type="text"
                value={customer.cpfCnpj}
                onChange={(e) =>
                  setCustomer((p) => ({
                    ...p,
                    cpfCnpj: maskCpfCnpj(e.target.value),
                  }))
                }
                placeholder="000.000.000-00"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                  customerErrors.cpfCnpj
                    ? 'border-red-400 bg-red-50'
                    : 'border-ink-6'
                }`}
              />
              {customerErrors.cpfCnpj && (
                <p className="text-xs text-red-500 mt-1">
                  {customerErrors.cpfCnpj}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-3 mb-1">
                Telefone (WhatsApp) <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                required
                value={customer.phone}
                onChange={(e) =>
                  setCustomer((p) => ({
                    ...p,
                    phone: maskPhone(e.target.value),
                  }))
                }
                placeholder="(11) 99999-0000"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                  customerErrors.phone
                    ? 'border-red-400 bg-red-50'
                    : 'border-ink-6'
                }`}
              />
              {customerErrors.phone && (
                <p className="text-xs text-red-500 mt-1">
                  {customerErrors.phone}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Address (conditional) */}
      {requireAddress && !prefilledAddress && (
        <div className="bg-paper rounded-xl border border-ink-7 p-5 mb-4 shadow-sm">
          <h3 className="text-sm font-semibold text-ink-2 mb-4">
            Endereço de entrega
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-ink-3 mb-1">
                CEP
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={address.cep}
                  onChange={(e) => {
                    const masked = maskCep(e.target.value)
                    setAddress((p) => ({ ...p, cep: masked }))
                    if (onlyDigits(masked).length === 8) fetchCep(masked)
                  }}
                  placeholder="00000-000"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                    addressErrors.cep
                      ? 'border-red-400 bg-red-50'
                      : 'border-ink-6'
                  }`}
                />
                {loadingCep && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-5 animate-spin" />
                )}
              </div>
              {addressErrors.cep && (
                <p className="text-xs text-red-500 mt-1">{addressErrors.cep}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-3 mb-1">
                Rua
              </label>
              <input
                type="text"
                value={address.street}
                onChange={(e) =>
                  setAddress((p) => ({ ...p, street: e.target.value }))
                }
                placeholder="Rua das Flores"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                  addressErrors.street
                    ? 'border-red-400 bg-red-50'
                    : 'border-ink-6'
                }`}
              />
              {addressErrors.street && (
                <p className="text-xs text-red-500 mt-1">
                  {addressErrors.street}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-ink-3 mb-1">
                  Número
                </label>
                <input
                  type="text"
                  value={address.number}
                  onChange={(e) =>
                    setAddress((p) => ({ ...p, number: e.target.value }))
                  }
                  placeholder="123"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                    addressErrors.number
                      ? 'border-red-400 bg-red-50'
                      : 'border-ink-6'
                  }`}
                />
                {addressErrors.number && (
                  <p className="text-xs text-red-500 mt-1">
                    {addressErrors.number}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-3 mb-1">
                  Complemento
                </label>
                <input
                  type="text"
                  value={address.complement}
                  onChange={(e) =>
                    setAddress((p) => ({ ...p, complement: e.target.value }))
                  }
                  placeholder="Apto 4B"
                  className="w-full px-3 py-2.5 border border-ink-6 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-3 mb-1">
                Bairro
              </label>
              <input
                type="text"
                value={address.neighborhood}
                onChange={(e) =>
                  setAddress((p) => ({ ...p, neighborhood: e.target.value }))
                }
                placeholder="Centro"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                  addressErrors.neighborhood
                    ? 'border-red-400 bg-red-50'
                    : 'border-ink-6'
                }`}
              />
              {addressErrors.neighborhood && (
                <p className="text-xs text-red-500 mt-1">
                  {addressErrors.neighborhood}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-ink-3 mb-1">
                  Cidade
                </label>
                <input
                  type="text"
                  value={address.city}
                  onChange={(e) =>
                    setAddress((p) => ({ ...p, city: e.target.value }))
                  }
                  placeholder="São Paulo"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                    addressErrors.city
                      ? 'border-red-400 bg-red-50'
                      : 'border-ink-6'
                  }`}
                />
                {addressErrors.city && (
                  <p className="text-xs text-red-500 mt-1">
                    {addressErrors.city}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-3 mb-1">
                  Estado
                </label>
                <div className="relative">
                  <select
                    value={address.state}
                    onChange={(e) =>
                      setAddress((p) => ({ ...p, state: e.target.value }))
                    }
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none appearance-none bg-paper transition-colors ${
                      addressErrors.state
                        ? 'border-red-400 bg-red-50'
                        : 'border-ink-6'
                    }`}
                  >
                    <option value="">UF</option>
                    {BRAZILIAN_STATES.map((uf) => (
                      <option key={uf} value={uf}>
                        {uf}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-5 pointer-events-none" />
                </div>
                {addressErrors.state && (
                  <p className="text-xs text-red-500 mt-1">
                    {addressErrors.state}
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-3 mb-1">
                Destinatário
              </label>
              <input
                type="text"
                value={address.recipient}
                onChange={(e) =>
                  setAddress((p) => ({ ...p, recipient: e.target.value }))
                }
                placeholder="Nome de quem vai receber (opcional)"
                className="w-full px-3 py-2.5 border border-ink-6 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors"
              />
            </div>
          </div>
        </div>
      )}

      {/* Payment method tabs */}
      <div className="bg-paper rounded-xl border border-ink-7 shadow-sm overflow-hidden">
        <div className="flex border-b border-ink-7">
          <button
            onClick={() => setActiveTab('CREDIT_CARD')}
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-medium transition-colors ${
              activeTab === 'CREDIT_CARD'
                ? 'text-brand-600 border-b-2 border-brand-600 bg-brand-50/50'
                : 'text-ink-4 hover:text-ink-2 hover:bg-ink-9'
            }`}
          >
            <CreditCard className="w-4 h-4" />
            <span>Cartão</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('PIX')
              if (!pixData && !loadingPix) handlePixPayment()
            }}
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-medium transition-colors ${
              activeTab === 'PIX'
                ? 'text-brand-600 border-b-2 border-brand-600 bg-brand-50/50'
                : 'text-ink-4 hover:text-ink-2 hover:bg-ink-9'
            }`}
          >
            <QrCode className="w-4 h-4" />
            <span>PIX</span>
          </button>
          {allowBoleto && (
            <button
              onClick={() => {
                setActiveTab('BOLETO')
                if (!boletoData && !loadingBoleto) handleBoletoPayment()
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-medium transition-colors ${
                activeTab === 'BOLETO'
                  ? 'text-brand-600 border-b-2 border-brand-600 bg-brand-50/50'
                  : 'text-ink-4 hover:text-ink-2 hover:bg-ink-9'
              }`}
            >
              <Barcode className="w-4 h-4" />
              <span>Boleto</span>
            </button>
          )}
        </div>

        <div className="p-5">
          {/* Error banner */}
          {state === 'error' && errorMessage && (
            <div className="flex items-start gap-3 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">
                  Erro no pagamento
                </p>
                <p className="text-sm text-red-600 mt-0.5">{errorMessage}</p>
                <button
                  onClick={() => {
                    setState('idle')
                    setErrorMessage('')
                  }}
                  className="text-sm text-red-700 underline mt-1"
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          )}

          {/* Tab: Credit Card */}
          {activeTab === 'CREDIT_CARD' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-ink-3 mb-1">
                  Nome no cartão
                </label>
                <input
                  type="text"
                  value={card.holderName}
                  onChange={(e) =>
                    setCard((p) => ({
                      ...p,
                      holderName: e.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="MARIA DA SILVA"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm uppercase focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                    cardErrors.holderName
                      ? 'border-red-400 bg-red-50'
                      : 'border-ink-6'
                  }`}
                />
                {cardErrors.holderName && (
                  <p className="text-xs text-red-500 mt-1">
                    {cardErrors.holderName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-3 mb-1">
                  Número do cartão
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={card.number}
                    onChange={(e) =>
                      setCard((p) => ({
                        ...p,
                        number: maskCardNumber(e.target.value),
                      }))
                    }
                    placeholder="0000 0000 0000 0000"
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm font-mono focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                      cardErrors.number
                        ? 'border-red-400 bg-red-50'
                        : 'border-ink-6'
                    }`}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <CardBrandIcon brand={detectCardBrand(card.number)} />
                  </div>
                </div>
                {cardErrors.number && (
                  <p className="text-xs text-red-500 mt-1">
                    {cardErrors.number}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-ink-3 mb-1">
                    Validade
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={card.expiry}
                    onChange={(e) =>
                      setCard((p) => ({
                        ...p,
                        expiry: maskExpiry(e.target.value),
                      }))
                    }
                    placeholder="MM/AA"
                    maxLength={5}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm font-mono focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                      cardErrors.expiry
                        ? 'border-red-400 bg-red-50'
                        : 'border-ink-6'
                    }`}
                  />
                  {cardErrors.expiry && (
                    <p className="text-xs text-red-500 mt-1">
                      {cardErrors.expiry}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-ink-3 mb-1">
                    CVV
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={card.cvv}
                    onChange={(e) =>
                      setCard((p) => ({
                        ...p,
                        cvv: e.target.value.replace(/\D/g, '').slice(0, 4),
                      }))
                    }
                    placeholder="123"
                    maxLength={4}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm font-mono focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                      cardErrors.cvv
                        ? 'border-red-400 bg-red-50'
                        : 'border-ink-6'
                    }`}
                  />
                  {cardErrors.cvv && (
                    <p className="text-xs text-red-500 mt-1">
                      {cardErrors.cvv}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-3 mb-1">
                  CPF/CNPJ do titular
                </label>
                <input
                  type="text"
                  value={card.cpfCnpj}
                  onChange={(e) =>
                    setCard((p) => ({
                      ...p,
                      cpfCnpj: maskCpfCnpj(e.target.value),
                    }))
                  }
                  placeholder="000.000.000-00"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                    cardErrors.cpfCnpj
                      ? 'border-red-400 bg-red-50'
                      : 'border-ink-6'
                  }`}
                />
                {cardErrors.cpfCnpj && (
                  <p className="text-xs text-red-500 mt-1">
                    {cardErrors.cpfCnpj}
                  </p>
                )}
              </div>
              {maxInstallments > 1 && (
                <div>
                  <label className="block text-xs font-medium text-ink-3 mb-1">
                    Parcelas
                  </label>
                  <div className="relative">
                    <select
                      value={installments}
                      onChange={(e) =>
                        setInstallments(parseInt(e.target.value))
                      }
                      className="w-full px-3 py-2.5 border border-ink-6 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none appearance-none bg-paper"
                    >
                      {getInstallmentOptions().map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-5 pointer-events-none" />
                  </div>
                </div>
              )}
              {/* Endereço de cobrança (CEP + Número) — só pra cartão quando não tem endereço completo */}
              {!requireAddress && !prefilledAddress && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-1 col-span-2">
                  <p className="text-xs text-amber-800 font-medium mb-3">
                    Endereço de cobrança (obrigatório para cartão)
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-ink-3 mb-1">CEP</label>
                      <input
                        type="text"
                        value={cardAddress.cep}
                        onChange={(e) => setCardAddress((p) => ({ ...p, cep: maskCep(e.target.value) }))}
                        placeholder="00000-000"
                        className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                          cardAddressErrors.cep ? 'border-red-400 bg-red-50' : 'border-ink-6'
                        }`}
                      />
                      {cardAddressErrors.cep && (
                        <p className="text-xs text-red-500 mt-1">{cardAddressErrors.cep}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-ink-3 mb-1">Número</label>
                      <input
                        type="text"
                        value={cardAddress.number}
                        onChange={(e) => setCardAddress((p) => ({ ...p, number: e.target.value }))}
                        placeholder="123"
                        className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-colors ${
                          cardAddressErrors.number ? 'border-red-400 bg-red-50' : 'border-ink-6'
                        }`}
                      />
                      {cardAddressErrors.number && (
                        <p className="text-xs text-red-500 mt-1">{cardAddressErrors.number}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={handleCardPayment}
                disabled={state === 'processing'}
                className="w-full mt-2 py-3 bg-brand-600 text-white rounded-lg font-semibold text-sm hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {state === 'processing' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {isSubscription ? 'Criando assinatura...' : 'Processando pagamento...'}
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    {isSubscription
                      ? `Assinar por ${formatCurrency(productAmount)}/mês`
                      : `Pagar ${formatCurrency(totalAmount)}`}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Tab: PIX */}
          {activeTab === 'PIX' && (
            <div className="space-y-4">
              {loadingPix && (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-brand-600 animate-spin mb-3" />
                  <p className="text-sm text-ink-4">Gerando QR Code...</p>
                </div>
              )}

              {pixData && !pixExpired && (
                <>
                  {/* QR Code */}
                  {pixData.qrCodeImage && (
                    <div className="flex justify-center">
                      <div className="p-3 bg-paper border-2 border-ink-7 rounded-xl">
                        <img
                          src={
                            pixData.qrCodeImage.startsWith('data:')
                              ? pixData.qrCodeImage
                              : `data:image/png;base64,${pixData.qrCodeImage}`
                          }
                          alt="QR Code PIX"
                          width={256}
                          height={256}
                          className="w-64 h-64"
                        />
                      </div>
                    </div>
                  )}

                  {/* Countdown */}
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <span className="text-ink-3">
                      Expira em{' '}
                      <span className="font-semibold text-amber-600">
                        {formatTime(pixTimeLeft)}
                      </span>
                    </span>
                  </div>

                  {/* Copy-paste field */}
                  {pixData.qrCodeText && (
                    <div>
                      <label className="block text-xs font-medium text-ink-3 mb-1">
                        Copia e cola
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          readOnly
                          value={pixData.qrCodeText}
                          className="w-full px-3 py-2.5 pr-20 border border-ink-6 rounded-lg text-xs font-mono bg-ink-9 text-ink-2"
                        />
                        <button
                          onClick={() =>
                            copyToClipboard(pixData.qrCodeText, 'pix')
                          }
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1 px-3 py-1.5 bg-brand-600 text-white rounded-md text-xs font-medium hover:bg-brand-700 transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                          {copiedField === 'pix' ? 'Copiado!' : 'Copiar'}
                        </button>
                      </div>
                    </div>
                  )}

                  <p className="text-center text-xs text-ink-5">
                    Aguardando confirmação do pagamento...
                  </p>
                </>
              )}

              {pixExpired && (
                <div className="text-center py-6 space-y-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <p className="text-sm font-medium text-ink-0">
                    QR Code expirado
                  </p>
                  <button
                    onClick={() => {
                      setPixData(null)
                      setPixExpired(false)
                      handlePixPayment()
                    }}
                    className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
                  >
                    Gerar novo
                  </button>
                </div>
              )}

              {!loadingPix && !pixData && state !== 'processing' && (
                <div className="text-center py-6">
                  <button
                    onClick={handlePixPayment}
                    className="px-6 py-3 bg-brand-600 text-white rounded-lg text-sm font-semibold hover:bg-brand-700 transition-colors flex items-center gap-2 mx-auto"
                  >
                    <QrCode className="w-4 h-4" />
                    Gerar QR Code PIX
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tab: Boleto */}
          {activeTab === 'BOLETO' && (
            <div className="space-y-4">
              {loadingBoleto && (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-brand-600 animate-spin mb-3" />
                  <p className="text-sm text-ink-4">Gerando boleto...</p>
                </div>
              )}

              {boletoData && (
                <>
                  {boletoData.dueDate && (
                    <div className="flex items-center gap-2 text-sm text-ink-3 bg-ink-9 rounded-lg p-3">
                      <Clock className="w-4 h-4 text-ink-5" />
                      Vencimento: {formatDate(boletoData.dueDate)}
                    </div>
                  )}

                  {boletoData.identificationField && (
                    <div>
                      <label className="block text-xs font-medium text-ink-3 mb-1">
                        Linha digitável
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          readOnly
                          value={boletoData.identificationField}
                          className="w-full px-3 py-2.5 pr-20 border border-ink-6 rounded-lg text-xs font-mono bg-ink-9 text-ink-2"
                        />
                        <button
                          onClick={() =>
                            copyToClipboard(
                              boletoData.identificationField,
                              'boleto'
                            )
                          }
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1 px-3 py-1.5 bg-brand-600 text-white rounded-md text-xs font-medium hover:bg-brand-700 transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                          {copiedField === 'boleto' ? 'Copiado!' : 'Copiar'}
                        </button>
                      </div>
                    </div>
                  )}

                  {boletoData.bankSlipUrl && (
                    <a
                      href={boletoData.bankSlipUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 py-3 bg-brand-600 text-white rounded-lg font-semibold text-sm hover:bg-brand-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Baixar PDF do boleto
                    </a>
                  )}

                  <p className="text-center text-xs text-ink-5">
                    O boleto pode levar até 3 dias úteis para compensar.
                  </p>
                </>
              )}

              {!loadingBoleto && !boletoData && state !== 'processing' && (
                <div className="text-center py-6">
                  <button
                    onClick={handleBoletoPayment}
                    className="px-6 py-3 bg-brand-600 text-white rounded-lg text-sm font-semibold hover:bg-brand-700 transition-colors flex items-center gap-2 mx-auto"
                  >
                    <Barcode className="w-4 h-4" />
                    Gerar boleto
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Security badge */}
        <div className="border-t border-ink-8 px-5 py-3 flex items-center justify-center gap-2 text-xs text-ink-5">
          <Lock className="w-3 h-3" />
          Pagamento seguro via Asaas
        </div>
      </div>
    </div>
  )
}
